import PropTypes from "prop-types";
import React, { Component, useMemo } from "react";
import DropDownMenu from "material-ui/DropDownMenu";
import CircularProgress from "material-ui/CircularProgress";
import theme, { themeOptions } from "styles/theme";
import DropDownIcon from "components/icons/caret";
import NewLabel from "./label-new";
import { Decorator as FormsyElement } from "formsy-react";
import { extractFormsyProps } from "lib/helpers";
import { DATATEST } from "constants/integration";

const styles = {
    base: {
        display: "inline-block",
    },
    container: {
        position: "relative",
        display: "inline-block",
        height: "40px",
        width: "100%",
    },
    containerLarge: {
        height: "50px",
    },
    input: {
        border: "1px solid " + themeOptions.palette.lightGrey,
        borderRadius: "4px",
        height: "40px",
        width: "100%",
        overflow: "hidden",
    },
    inputLarge: {
        height: "50px",
    },
    baseLabel: {
        marginTop: "4px",
    },
    inputDisabled: {
        border: "1px solid " + themeOptions.palette.lighterGrey,
    },
    dropdownLabel: {
        lineHeight: "40px",
        top: "-1px",
        paddingLeft: "16px",
        paddingRight: "40px",
        color: themeOptions.palette.darkGrey,
        overflow: "hidden",
        textOverflow: "ellipsis",
        height: "100%",
        whiteSpace: "nowrap",
    },
    dropdownLabelWithIcon: {
        lineHeight: "40px",
        top: "-1px",
        paddingLeft: "30px",
        paddingRight: "40px",
        color: themeOptions.palette.darkGrey,
        overflow: "hidden",
        textOverflow: "ellipsis",
        height: "100%",
        whiteSpace: "nowrap",
    },
    dropdownLabelLarge: {
        lineHeight: "50px",
    },
    dropdownLabelDisabled: {
        color: themeOptions.palette.grey,
    },
    iconStyle: {
        display: "none",
    },
    dropDownIcon: {
        position: "absolute",
        top: "50%",
        right: "8px",
        cursor: "pointer",
        transform: "translateY(-50%)",
        pointerEvents: "none",
    },
    loading: {
        position: "absolute",
        top: "50%",
        right: "12px",
        zIndex: 2,
        cursor: "pointer",
        transform: "translateY(-50%)",
    },
    error: {
        border: "1px solid " + themeOptions.palette.error,
    },
    errorStyle: {
        color: themeOptions.palette.error,
        fontSize: "12px",
        paddingTop: "10px",
        lineHeight: "12px",
    },
};

const ErrorMessage = ({ errorText, errorStyle = {} }) => {
    const style = useMemo(() => ({ ...styles.errorStyle, ...errorStyle }), [errorStyle]);
    if (errorText) {
        return (
            <div className="error-text" data-test={DATATEST.SELECT_FIELD_ERROR} style={style}>
                {errorText}
            </div>
        );
    }
    return null;
};

/** @augments {Component<any, any>} */
@FormsyElement()
class NewField extends Component {
    static propTypes = {
        children: PropTypes.node,
        large: PropTypes.bool,
        width: PropTypes.string,
        errorText: PropTypes.string,
        label: PropTypes.node,
        customLabel: PropTypes.node,
        loading: PropTypes.bool,
        badge: PropTypes.number,
        name: PropTypes.string.isRequired,
        key: PropTypes.string,
        onChange: PropTypes.func,
        setValue: PropTypes.func, //Reserved function for formsy validations
        disabled: PropTypes.bool,
        dataTest: PropTypes.string,
        leftIcon: PropTypes.object,
        iconStyle: PropTypes.object,
        customLabelStyle: PropTypes.object,
        labelStyle: PropTypes.object,
        disabledStyle: PropTypes.object,
        disabledLabelStyle: PropTypes.object,
        disabledDropDownIconStyle: PropTypes.object,
    };

    static defaultProps = {
        style: {},
        large: false,
        width: "auto",
        loading: false,
        name: "select-field",
        disabled: false,
    };

    handleChangeEvent(event, index, value) {
        const { onChange, setValue } = this.props;
        setValue(value); //Formsy function that sets form values on input change
        if (onChange) {
            onChange(event, value);
        }
    }

    renderLabel() {
        const { label } = this.props;
        if (label) {
            return <div style={Object.assign({}, theme.type.label, styles.label)}>{label}</div>;
        }
        return null;
    }

    render() {
        let {
            children,
            baseClassName,
            style,
            label,
            customLabel,
            name,
            key,
            large,
            width,
            loading,
            errorText,
            badge,
            disabled,
            inputStyle,
            hideIcon,
            labelClassName,
            labelStyle,
            customLabelStyle,
            containerStyle,
            errorOutline = true,
            dataTest,
            errorStyle,
            iconStyle,
            leftIcon,
            disabledStyle = {},
            disabledLabelStyle = {},
            disabledDropDownIconStyle = {},
            ...other
        } = this.props;
        other = extractFormsyProps(other);

        let rightIcon;
        if (loading) {
            rightIcon = (
                <CircularProgress style={styles.loading} color={themeOptions.palette.grey} size={16} thickness={1} />
            );
        } else if (!hideIcon) {
            rightIcon = (
                <DropDownIcon
                    style={{ ...styles.dropDownIcon, ...(disabled ? disabledDropDownIconStyle : {}), ...iconStyle }}
                />
            );
        }

        const dropdownLabelStyle = leftIcon ? styles.dropdownLabelWithIcon : styles.dropdownLabel;

        return (
            <div className={baseClassName} style={Object.assign({}, styles.base, { width }, style)}>
                {customLabel ||
                    (label && (
                        <NewLabel
                            style={{ ...labelStyle, ...customLabelStyle }}
                            className={labelClassName}
                            badge={badge}
                        >
                            {label}
                        </NewLabel>
                    ))}
                <div style={Object.assign({}, styles.container, large && styles.containerLarge, containerStyle)}>
                    {leftIcon}
                    <DropDownMenu
                        data-test={dataTest}
                        key={key || name}
                        ref="dropdown"
                        className="muiDropdown"
                        underlineStyle={{ display: "none" }}
                        onChange={this.handleChangeEvent.bind(this)}
                        {...other}
                        style={Object.assign(
                            {},
                            styles.input,
                            large && styles.inputLarge,
                            this.props.disabled && Object.assign({}, styles.inputDisabled, disabledStyle),
                            inputStyle,
                            errorText && errorOutline && styles.error,
                        )}
                        labelStyle={Object.assign(
                            {},
                            dropdownLabelStyle,
                            large && styles.dropdownLabelLarge,
                            labelStyle,
                            this.props.disabled && Object.assign({}, styles.dropdownLabelDisabled, disabledLabelStyle),
                        )}
                        iconStyle={styles.iconStyle}
                        menuStyle={{ width: width, maxHeight: "20em" }}
                        name={name || label}
                        disabled={disabled}
                    >
                        {children}
                    </DropDownMenu>
                    {rightIcon}
                </div>
                <ErrorMessage errorText={errorText} errorStyle={errorStyle} />
            </div>
        );
    }
}

NewField.displayName = "Select";

export default NewField;
